import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Search,
  Loader2,
  AlertCircle,
  Check
} from 'lucide-react';
import inventoryService from '../../../services/inventoryService';

function OverrideProductDialog({ open, onClose, onConfirm, originalProduct }) {
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const searchProducts = useCallback(async () => {
    if (!searchText.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const filters = { product_code: searchText.trim() };
      const response = await inventoryService.getInventory(1, 50, 'name', 'asc', filters, null);

      const grouped = {};
      response.inventory?.forEach(item => {
        const code = item.product_code || item.product_id;
        if (!grouped[code]) {
          grouped[code] = {
            product_id: item._id || item.product_id,
            product_code: code,
            product_name: item.product_name || item.name,
            specification: item.specification || '',
            unit: item.unit || '',
            category: item.category,
            locations: [],
            totalAvailable: 0
          };
        }

        const qty = item.remaining_quantity || item.available_quantity || 0;
        grouped[code].locations.push({
          location: item.location || 'main',
          available_quantity: qty,
          source_client_id: item.source_client_id,
          source_client_name: item.source_client_name
        });
        grouped[code].totalAvailable += qty;
      });

      setSearchResults(Object.values(grouped));
    } catch (err) {
      setError(err.response?.data?.error || '搜索产品失败');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchText]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (open) {
        searchProducts();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText, open, searchProducts]);

  useEffect(() => {
    if (open) {
      setSearchText('');
      setSearchResults([]);
      setError(null);
      setSelectedProduct(null);
    }
  }, [open]);

  const handleConfirm = () => {
    if (!selectedProduct) return;

    const replacementProduct = {
      product_id: selectedProduct.product_code,
      product_code: selectedProduct.product_code,
      product_name: selectedProduct.product_name,
      specification: selectedProduct.specification,
      unit: selectedProduct.unit,
      category: selectedProduct.category,
      locations: selectedProduct.locations,
      locationAllocations: []
    };

    onConfirm(replacementProduct);
  };

  const handleClose = () => {
    setSearchText('');
    setSearchResults([]);
    setError(null);
    setSelectedProduct(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            选择替代产品
            {originalProduct && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                (原产品: {originalProduct.product_code})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4 space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="输入产品编码搜索..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">产品编码</th>
                    <th className="text-left p-3 font-medium">产品名称</th>
                    <th className="text-left p-3 font-medium">规格</th>
                    <th className="text-left p-3 font-medium">可用库存</th>
                    <th className="text-left p-3 font-medium">库位</th>
                    <th className="text-left p-3 font-medium w-24">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">搜索中...</p>
                      </td>
                    </tr>
                  ) : searchResults.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8 text-muted-foreground">
                        {searchText.trim() ? '未找到匹配的产品' : '请输入产品编码进行搜索'}
                      </td>
                    </tr>
                  ) : (
                    searchResults.map((product) => (
                      <tr
                        key={product.product_code}
                        className={selectedProduct?.product_code === product.product_code ? 'bg-muted' : 'hover:bg-muted/50'}
                      >
                        <td className="p-3">
                          <span className="font-mono font-medium">{product.product_code}</span>
                        </td>
                        <td className="p-3">{product.product_name || '-'}</td>
                        <td className="p-3 text-muted-foreground">{product.specification || '-'}</td>
                        <td className="p-3">
                          <Badge
                            variant={product.totalAvailable > 0 ? 'success' : 'destructive'}
                            className="font-semibold"
                          >
                            {product.totalAvailable}{product.unit || '支'}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {product.locations.map((loc, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {loc.location}: {loc.available_quantity}{product.unit || '支'}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant={selectedProduct?.product_code === product.product_code ? 'default' : 'outline'}
                            onClick={() => setSelectedProduct(product)}
                            className="gap-1"
                          >
                            {selectedProduct?.product_code === product.product_code ? (
                              <>
                                <Check className="h-3 w-3" />
                                已选
                              </>
                            ) : (
                              '选择'
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedProduct}
          >
            确认替代
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OverrideProductDialog;
